import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MobileModal from '../../../../components/compositions/MobileModal/MobileModal';
import { useQuery } from 'react-query';
import moment from 'moment';
import { PartnerScheduleAvailableDate } from '../../../../apis/models/partner-schedules/res/PartnerScheduleAvailableDate';
import useProductOptionsStore from '../../../../store/useProductOptionsStore';
import {
  fetchAvailableReservationDates,
  fetchAvailableReservationTimes,
  fetchEmployeeSchedules,
} from '../../../../apis/partner-schedules';
import {
  getEarlyAvailableDateWhenDisabled,
  getPartnerName,
  getProductOptionsPaymentPriceInfos,
  isValidReservationChannel,
  isValidReservationPushChannel,
} from '../../../../utils/utils';
import { PartnerScheduleTime } from '../../../../apis/models/partner-schedules/res/PartnerScheduleTime';
import AppError from '../../../../apis/models/common/res/AppError';
import useAuthStore from '../../../../store/useAuthStore';
import ReservationCalendar from '../../../../components/compositions/ReservationCalendar';
import ReservationTimeSelectRadioGroup from '../../../../components/compositions/ReservationTimeSelectRadioGroup';
import Button from '../../../../components/atom/Button';
import strings from '../../../../constants/strings.constants';
import colors from '../../../../constants/colors.constants';
import Typo from '../../../../components/atom/Typo';
import TextArea from '../../../../components/atom/TextArea';
import AddedProductOptionItem from './AddedProductOptionItem';
import {
  addReservation,
  getUtmQueries,
  sendReservationCompleteMessage,
} from '../../../../apis/reservations';
import { AddReservationBody } from '../../../../apis/models/reservations/req/AddReservationBody';
import { ROUTER_PATHS } from '../../../../routes/routes.constatns';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ReservationChannel } from '../../../../apis/models/reservations/res/Reservation';
import useAlertStore from '../../../../store/useAlertStore';
import useLanguageCodeStore, {
  LanguageCode,
} from '../../../../store/useLanguageCodeStore';
import AuthBottomSheet from '../../../../components/compositions/AuthBottomSheet/AuthBottomSheet';
import { fetchConsultingCategories } from '../../../../apis/consulting-categories';
import { ReactComponent as AlertIcon } from '../../../../assets/svgs/icon-exclamation.svg';
import { Fade } from 'react-awesome-reveal';
import {
  sendTutorialReservationCompleteEvent,
  sendTutorialReservationDateSelectedEvent,
  useTutorialStore,
} from '../../../../store/useTutorialStore';
import { ChatbotChannel } from '../../../../apis/models/auth/req/ChatbotGuestSignInBody';
import URL_QUERY_KEYS from '../../../../constants/urlQueryKeys.constants';
import {
  UNAVAILABLE_PRODUCT_OPTION,
  isUnavailableProductOptionData,
} from '../../../../apis/models/reservations/res/UnavailableProductOption';
import {
  getRejectedProductOptionIds,
  toProductOptionExhibitions,
} from '../../../../utils/product-option-exhibition';
import { sendPurchaseEvent } from '../../../../utils/ga-utils';
import { validateSelectedProductOptionsForSubmit } from '../../../../shared/hooks/useSelectedProductOptionsValidation';

export interface DateSelectModalProps {
  onCancel: () => void;
  visible?: boolean;
}

function DateSelectModal(props: DateSelectModalProps) {
  const { visible = false, onCancel } = props;
  const { productOptions, clear, removeMany } = useProductOptionsStore();
  const { partner, customer, channel } = useAuthStore();
  const [currentCalendarVisibleMonth, setCurrentCalendarVisibleMonth] =
    useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [timeBlocks, setTimeBlocks] = useState<PartnerScheduleTime[]>([]);
  const [selectedTime, setSelectedTime] = useState<string>();
  const [availableDates, setAvailableDates] = useState<
    Record<string, PartnerScheduleAvailableDate>
  >({});
  const [autoSelectDateErrorTimes, setAutoSelectDateErrorTimes] = useState(0);
  const [step, setStep] = useState<number>(1);
  const [consultingMemo, setConsultingMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlertStore();
  const { languageCode } = useLanguageCodeStore();
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [searchParams] = useSearchParams();
  const [selectedConsultingCategories, setSelectedConsultingCategories] =
    useState<string[]>([]);
  const location = useLocation();
  const { nextStep } = useTutorialStore();
  const isTut = !!searchParams.get('tut');
  const customLabel =
    partner?.productViewConfig?.mimoWebViewConfig?.customLabel;

  const { data: consultingCategories = [] } = useQuery({
    queryKey: ['FETCH_CONSULTING_CATEGORIES'],
    queryFn: async () => {
      if (partner) {
        return fetchConsultingCategories(partner.id);
      }
      return [];
    },
    retry: false,
    enabled: !!partner,
    refetchOnWindowFocus: false,
  });

  const isKoreanLang = languageCode === 'ko' || !languageCode;

  useEffect(() => {
    if (visible) {
      // 초기화
      setCurrentCalendarVisibleMonth(new Date());
      setSelectedTime(undefined);
      setStep(1);
      setConsultingMemo('');
    }
  }, [visible]);

  const reservationAvailableDatesQuery = useQuery({
    queryKey: [
      moment(currentCalendarVisibleMonth).format('YYYY-MM'),
      productOptions,
    ],
    queryFn: async () => {
      const momentDate = moment(currentCalendarVisibleMonth);
      const year = momentDate.get('year');
      const month = momentDate.get('month') + 1;
      // 베가스 프로모션 페이지로 진입시 (데모 챗봇 모드의 경우 Mock 상품처리)
      let isTempData: boolean | undefined;
      if (searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true') {
        isTempData = true;
      }
      return fetchAvailableReservationDates({
        year,
        month,
        productOptionIdList: isTempData
          ? []
          : productOptions.map(({ id }) => id),
        partnerCode: partner?.partnerCode || '',
        isConsultingReservation: false,
      });
    },
    onSuccess: (data) => {
      setAvailableDates(data);
      if (selectedDate) {
        const updatedDate = getEarlyAvailableDateWhenDisabled(
          selectedDate,
          data,
        );
        if (updatedDate) {
          setSelectedDate(updatedDate);
        }
      }
    },
    retry: false,
    enabled: !!partner && visible,
    refetchOnWindowFocus: false,
  });

  const reservationTimeBlocksQuery = useQuery({
    queryKey: [
      moment(selectedDate).format('YYYY-MM-DD'),
      { partnerCustomerId: customer?.partnerCustomerId },
      productOptions,
    ],
    queryFn: async () => {
      const dateParam = moment(selectedDate).format('YYYY-MM-DD');
      // 베가스 프로모션 페이지로 진입시 (데모 챗봇 모드의 경우 Mock 상품처리)
      let isTempData: boolean | undefined;
      if (searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true') {
        isTempData = true;
      }
      //
      return fetchAvailableReservationTimes({
        targetDate: dateParam,
        productOptionIdList: isTempData
          ? []
          : productOptions.map(({ id }) => id),
        partnerCode: partner?.partnerCode || '',
        isConsultingReservation: false,
        partnerCustomerId: customer?.partnerCustomerId,
      });
    },
    onSuccess: (data) => {
      setTimeBlocks(data);
    },
    onError: (error) => {
      const appError = error as AppError;
      if (appError.statusCode === 400 && autoSelectDateErrorTimes <= 3) {
        const momentDate = moment(selectedDate).add(1, 'd');
        setSelectedDate(momentDate.toDate());
        setAutoSelectDateErrorTimes(autoSelectDateErrorTimes + 1);
      }
    },
    retry: false,
    enabled: !!partner && visible,
    refetchOnWindowFocus: false,
  });

  const { data: blockedEmployeeSchedules } = useQuery({
    queryKey: [
      `EMPLOYEE_SCHEDULE_${moment(selectedDate).format('YYYY-MM-DD')}`,
      productOptions,
    ],
    queryFn: async () => {
      const dateParam = moment(selectedDate).format('YYYY-MM-DD');
      // 베가스 프로모션 페이지로 진입시 (데모 챗봇 모드의 경우 Mock 상품처리)
      let isTempData: boolean | undefined;
      if (searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true') {
        isTempData = true;
      }
      if (partner) {
        return fetchEmployeeSchedules(partner.partnerCode, {
          date: dateParam,
          productOptionIds: isTempData
            ? []
            : productOptions.map(({ id }) => id),
          languageCode:
            languageCode === LanguageCode.ko ? undefined : languageCode,
        });
      }
      return {
        rows: [],
        count: 0,
      };
    },
    retry: false,
    enabled:
      !!partner &&
      visible &&
      !!selectedDate &&
      productOptions &&
      productOptions.length > 0,
    refetchOnWindowFocus: false,
  });

  const isConsultingReservationAutoAccept = useMemo(() => {
    if (partner) {
      const { homepageConfig } = partner;
      return !!homepageConfig?.consultingReservationAutoAccept;
    }
    return false;
  }, [partner]);

  const isTreatmentReservationAutoAccept = useMemo(() => {
    if (partner) {
      const { homepageConfig } = partner;
      return !!homepageConfig?.treatmentReservationAutoAccept;
    }
    return false;
  }, [partner]);

  const getModalTitle = useCallback(() => {
    switch (step) {
      case 1:
        return strings.selectDate;
      case 2:
        return strings.requestReservation;
      default:
        return strings.selectDate;
    }
  }, [step]);

  const isAllReservationTimeBlockedByEmployeeSchedule = () => {
    if (blockedEmployeeSchedules) {
      const { rows } = blockedEmployeeSchedules;
      return rows.length > 0;
    }

    return false;
  };

  const renderReservationBlockReasons = () => {
    if (blockedEmployeeSchedules) {
      const { rows } = blockedEmployeeSchedules;
      if (rows.length > 0) {
        return (
          <div
            className="mt-4 p-3 rounded-[12px]"
            style={{
              backgroundColor: '#FEF2F2',
            }}
          >
            <div className="flex flex-col items-center mb-4 gap-2">
              <AlertIcon width={24} height={24} />
              <Typo
                type="m-16"
                style={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                {strings.noAvailableReservationTime}
              </Typo>
            </div>

            <div className="mt-2 mb-2 bg-white p-[10px] rounded-[12px]">
              <div className="mb-1">
                <Typo type="m-14">{strings.unavailableTreatment}</Typo>
              </div>

              <div className="ml-1">
                {rows.map(({ productOptionName, displayName }) => {
                  return (
                    <div>
                      <Typo type="r-14">
                        • {displayName || productOptionName}
                      </Typo>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-white p-[10px] rounded-[12px]">
              <div className="flex flex-col gap-2">
                <Typo
                  type="r-14"
                  style={{
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  • {strings.noAvailableReservationTimeDesc1}
                </Typo>
                <Typo
                  type="r-14"
                  style={{
                    lineHeight: 1.2,
                  }}
                >
                  • {strings.noAvailableReservationTimeDesc2}
                </Typo>
              </div>
            </div>
          </div>
        );
      }
    }

    return '';
  };

  const shouldRenderExtraConsultingCategories = () => {
    if (partner) {
      let useExtraConsultingCategory = false;
      const { chatBotConfig } = partner;
      if (chatBotConfig) {
        useExtraConsultingCategory =
          chatBotConfig.useExtraConsultingCategory || false;
      }
      return useExtraConsultingCategory && consultingCategories.length > 0;
    }

    return false;
  };

  const getMemoParam = () => {
    const chatbotChannel =
      searchParams.get('chatbotChannel') ||
      searchParams.get('chatBotChannel') ||
      undefined;
    const respondIOChannelName =
      searchParams.get('respondIOChannelName') || undefined;
    const userKey = searchParams.get('userKey') || undefined;
    const userName = searchParams.get('userName') || undefined;
    let memo = '';
    if (consultingMemo) {
      memo += consultingMemo;
    }
    if (selectedConsultingCategories.length > 0) {
      memo += `${memo ? '\n' : ''}-추가상담요청 : ${selectedConsultingCategories.join(',')}`;
    }
    if (chatbotChannel && userKey && userName) {
      memo += `${memo ? '\n\n' : ''}-RESERVATION CHANNEL : ${chatbotChannel}${
        respondIOChannelName
          ? ` (${respondIOChannelName.replace('+', ' ')})`
          : ''
      }\n-${chatbotChannel} USER NAME : ${userName}`;
    }
    return memo;
  };

  const makeReservation = async (token: string) => {
    let submittedOptions = productOptions;
    setSubmitting(true);
    try {
      // 베가스 프로모션 페이지로 진입시 (데모 챗봇 모드의 경우 Mock 상품처리)
      let isTempData: boolean | undefined;
      if (searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true') {
        isTempData = true;
      }
      if (!isTempData && productOptions.length > 0) {
        // 제출 직전 항상 서버로 재검증한다. 변경·제외가 있으면 제출하지 않고 예약 화면으로 돌아간다.
        const validation = await validateSelectedProductOptionsForSubmit();
        if (validation.status === 'changed') {
          onCancel();
          return;
        }
        if (validation.status === 'stale') {
          return;
        }
        submittedOptions = validation.productOptions;
      }
      let reservationChannel: ReservationChannel | undefined =
        ReservationChannel.MIMO;
      if (isValidReservationChannel(channel)) {
        reservationChannel = channel as ReservationChannel;
      }
      let reservationPushChannel: ChatbotChannel | undefined;
      let respondIOUserKey: string | undefined;
      const chatbotChannel =
        searchParams.get('chatbotChannel') ||
        searchParams.get('chatBotChannel') ||
        undefined;
      if (isValidReservationPushChannel(chatbotChannel)) {
        reservationPushChannel = chatbotChannel as ChatbotChannel;
        const userKey = searchParams.get('userKey') || undefined;
        if (chatbotChannel === ChatbotChannel.RESPOND_IO && userKey) {
          respondIOUserKey = userKey;
        }
      }
      const reservedAt =
        moment(selectedDate).format('YYYY-MM-DD') + ` ${selectedTime}`;
      const body: AddReservationBody = {
        productOptionIds: isTempData
          ? []
          : submittedOptions.map(({ id }) => id),
        productOptionExhibitions: isTempData
          ? []
          : toProductOptionExhibitions(submittedOptions),
        expectPrice:
          getProductOptionsPaymentPriceInfos(submittedOptions).totalExpectPrice,
        reservationMemo: getMemoParam(),
        reservedAt,
        reservationChannel,
        needConsultation: !(submittedOptions.length > 0),
        reservationPushChannel,
        ...getUtmQueries(searchParams),
        languageCode:
          languageCode === LanguageCode.ko ? undefined : languageCode,
        respondIOUserKey,
      };
      const row = await addReservation(body, token);

      if (!isTut && !isTempData) {
        sendPurchaseEvent(row, submittedOptions);
      }

      if (isTut) {
        setTimeout(() => {
          nextStep();
        }, 500);
        sendTutorialReservationCompleteEvent(row);
      }

      const { id: reservationId } = row;
      const chatLogId = searchParams.get('chatLogId') || undefined;
      if (chatLogId && partner) {
        try {
          const chatId = parseInt(chatLogId);
          sendReservationCompleteMessage(partner.id, token, {
            reservationId,
            chatLogId: chatId,
            reservationType: 'REQUEST',
          }).then(() => {
            // Do nothing
          });
        } catch (e) {
          console.error(e);
          // Do nothing
        }
      }
      clear();
      navigate(`${ROUTER_PATHS.RESERVATION_COMPLETE}${location.search}`, {
        state: { ...row, reservationProductOptions: [...submittedOptions] },
      });
    } catch (e) {
      const error = e as AppError;
      if (
        error.code === UNAVAILABLE_PRODUCT_OPTION &&
        isUnavailableProductOptionData(error.data)
      ) {
        // 제출 스냅샷 중 서버가 거절한 옵션만 제거한다. 자동 재요청·상담 예약 전환은 하지 않는다.
        const rejectedIds = getRejectedProductOptionIds(
          submittedOptions,
          error.data,
        );
        if (rejectedIds.length > 0) {
          removeMany(rejectedIds);
        }
        if (error.message) {
          showAlert(error.message);
        }
        onCancel();
        return;
      }
      if (error.message) {
        showAlert(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      setStep(2);
      if (isTut) {
        setTimeout(() => {
          nextStep();
        }, 500);
        sendTutorialReservationDateSelectedEvent();
      }
    } else {
      if (customer) {
        await makeReservation(customer.token);
      } else {
        setAuthModalVisible(true);
      }
    }
  };

  const getReservationDateText = () => {
    if (selectedDate && selectedTime) {
      return `${moment(selectedDate).format('YYYY.MM.DD. (dd)')} ${selectedTime}`;
    }

    return '';
  };

  const partnerName = useMemo(() => getPartnerName(partner), [partner]);

  const renderContents = () => {
    if (step === 1) {
      return (
        <div
          id="tutorial-date-select"
          style={{
            paddingLeft: 20,
            paddingBottom: 20,
            paddingRight: 20,
          }}
        >
          <ReservationCalendar
            value={selectedDate || undefined}
            loading={reservationAvailableDatesQuery.isLoading}
            availableDates={availableDates}
            onDateSelect={(date) => {
              setSelectedDate(date);
              setSelectedTime(undefined);
            }}
            onMonthChange={(date) => {
              setCurrentCalendarVisibleMonth(date);
            }}
          />

          {isAllReservationTimeBlockedByEmployeeSchedule() ? (
            <Fade duration={200}>{renderReservationBlockReasons()}</Fade>
          ) : (
            <Fade duration={200}>
              <ReservationTimeSelectRadioGroup
                loading={reservationTimeBlocksQuery.isLoading}
                timeBlocks={timeBlocks}
                value={selectedTime}
                onTimeSelect={(timeText) => {
                  setSelectedTime(timeText);
                  if (isTut) {
                    nextStep();
                  }
                }}
              />
            </Fade>
          )}
        </div>
      );
    }

    if (step === 2) {
      return (
        <div
          className="flex flex-col gap-3 h-full"
          style={{
            backgroundColor: colors.greyHover,
          }}
        >
          <div className="bg-white py-6 px-5">
            <div>
              <Typo type="m-16">{strings.reservationVerifyTitle}</Typo>
            </div>
            {!isConsultingReservationAutoAccept &&
              !isTreatmentReservationAutoAccept && (
                <Typo type="r-12" color={colors.subTitle}>
                  {customLabel?.reservationRequestNotice ||
                    strings.reservationRequestNotice}
                </Typo>
              )}
            <div
              className="flex flex-col gap-2"
              style={{
                padding: '14px 16px',
                backgroundColor: colors.greyHover,
                marginTop: 16,
                borderRadius: 12,
              }}
            >
              <Typo type="b-18">{getReservationDateText()}</Typo>
              <Typo type="r-16">{partnerName}</Typo>
            </div>
          </div>

          {shouldRenderExtraConsultingCategories() && isKoreanLang && (
            <div className="bg-white py-6 px-5 flex flex-col gap-4">
              <Typo type="m-16">
                {strings.extraConsultingCategoryTitle}
                <span
                  style={{
                    marginLeft: 4,
                    color: colors.slate500,
                    fontWeight: 400,
                  }}
                >
                  {strings.extraConsultingCategoryTitlePostFix}
                </span>
              </Typo>
              <div className="flex gap-1 flex-wrap">
                {consultingCategories.map(({ id, name }) => {
                  const isSelected = !!selectedConsultingCategories.find(
                    (selectedName) => {
                      return selectedName === name;
                    },
                  );
                  return (
                    <Button
                      size="small"
                      style={{
                        height: 38,
                        background: isSelected ? colors.slate800 : 'white',
                        color: isSelected ? 'white' : colors.slate500,
                        fontWeight: isSelected ? 500 : 300,
                        border: '1px solid',
                        borderColor: isSelected
                          ? colors.slate800
                          : colors.defaultBorder,
                      }}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedConsultingCategories(
                            selectedConsultingCategories.filter(
                              (selectedName) => selectedName !== name,
                            ),
                          );
                        } else {
                          setSelectedConsultingCategories([
                            ...selectedConsultingCategories,
                            name,
                          ]);
                        }
                      }}
                      key={id}
                    >
                      {name}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-white py-6 px-5 flex flex-col gap-4">
            <Typo type="m-16">
              {customLabel?.consultingMemo || strings.consultingMemo}
            </Typo>
            <TextArea
              placeholder={
                customLabel?.consultingMemoPlaceholder ||
                strings.consultingMemoPlaceholder
              }
              value={consultingMemo}
              onChange={(value) => {
                setConsultingMemo(value);
              }}
            />
          </div>

          <div className="bg-white py-6 px-5">
            <div className="flex justify-between items-center">
              <Typo type="m-16">{strings.selectedTreatments}</Typo>
              <Typo type="r-16">
                {productOptions.length}
                {strings.unitEA}
              </Typo>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              {productOptions.map((option) => (
                <AddedProductOptionItem
                  key={option.id}
                  productOption={option}
                  customLabels={{
                    zeroPriceLabel:
                      partner?.productViewConfig?.zeroPriceReplaceText,
                    needConsultationLabel:
                      partner?.productViewConfig?.consultationReplaceText,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }
  };

  const renderFooterButton = () => {
    if (step === 1) {
      return (
        <Button
          id="tutorial-date-select-next"
          disabled={!selectedDate || !selectedTime}
          style={{
            width: '100%',
          }}
          onClick={handleNext}
        >
          {strings.next}
        </Button>
      );
    }
    if (step === 2) {
      return (
        <Button
          id="tutorial-reservation-button"
          disabled={submitting}
          style={{
            width: '100%',
          }}
          onClick={handleNext}
        >
          {strings.requestReservation}
        </Button>
      );
    }
  };

  return (
    <MobileModal
      visible={visible}
      onCancel={onCancel}
      title={getModalTitle()}
      backButtonAction={() => {
        if (step === 1) {
          if (onCancel) onCancel();
        } else {
          setStep(1);
        }
      }}
      footer={
        <div
          style={{
            padding: '16px 20px',
            boxShadow: '0px -4px 30px 0px #0000001f',
          }}
        >
          {renderFooterButton()}
        </div>
      }
    >
      {renderContents()}

      <AuthBottomSheet
        visible={authModalVisible}
        onCancel={() => {
          setAuthModalVisible(false);
        }}
        onAuthComplete={async (token: string) => {
          await makeReservation(token);
        }}
      />
    </MobileModal>
  );
}

export default DateSelectModal;
